import Papa from 'papaparse'
import { controlFile, packageFiles } from '../data/world/package'
import { parseJson } from './csv'
import type { Row } from './csv'

/*
 * Comprobación del paquete completo contra 00_control.csv, con su estructura exacta:
 * - 1 fila record_type = "dataset": metadatos e instrucciones en details_json (no es un archivo).
 * - 47 filas record_type = "file": una por archivo 01–47.
 * - foreign_keys_json: objeto { columna_origen: "tabla_destino" }.
 * - Clave primaria de cada tabla: details_json.primary_key ("id"; "map_id" en map_images).
 * - column_types_json: solo las columnas declaradas numéricas se comparan como número.
 *   Los identificadores de texto (p. ej. "7,1", "14,23@1") se comparan exactamente.
 *   Una celda vacía es "sin dato", nunca cero.
 * Todo se calcula con los archivos descargados en este navegador; nada se copia de los esperados.
 */

export const EXPECTED_FILE_ROWS = 47
export const EXPECTED_DATASET_ROWS = 1

export type FkResult = { column: string; targetTable: string; targetColumn: string; checked: number; missing: number; samples: string[]; numeric: boolean; error?: string }

export type FileCheck = {
  filename: string
  table?: string
  primaryKey: string
  loaded: boolean
  error?: string
  rows?: number
  expectedRows?: number
  bytes?: number
  expectedBytes?: number
  sha256?: string
  expectedSha?: string
  expectedColumns: string[]
  missingColumns: string[]
  extraColumns: string[]
  numericColumns: string[]
  /** Celdas no vacías de columnas numéricas que no son número. */
  nonNumeric: { column: string; count: number; samples: string[] }[]
  duplicateIds: number
  duplicateSamples: string[]
  emptyIds: number
  parseErrors: number
  parseErrorSamples: string[]
  fks: FkResult[]
  controlProblems: string[]
}

export type PackageReport = {
  controlLoaded: boolean
  controlError?: string
  controlRows: number
  recordTypes: Record<string, number>
  dataset?: { details: unknown; raw?: string; parseError?: string }
  /** Problemas de la propia estructura del control (filas, JSON inválido…). */
  controlProblems: string[]
  files: FileCheck[]
  notInControl: string[]
  notInPackage: string[]
  checkedAt: string
}

async function sha256Hex(buf: ArrayBuffer): Promise<string | undefined> {
  try {
    const d = await crypto.subtle.digest('SHA-256', buf)
    return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return undefined
  }
}

type ParseErr = { row?: number; message: string }
type Parsed = { rows: Row[]; fields: string[]; errors: ParseErr[]; bytes: number; sha?: string }

async function fetchParsed(url: string): Promise<Parsed> {
  let res: Response
  try {
    res = await fetch(url)
  } catch (e) {
    throw new Error(`Descarga fallida (${e instanceof Error ? e.message : String(e)})`)
  }
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)
  const buf = await res.arrayBuffer()
  const sha = await sha256Hex(buf)
  const text = new TextDecoder('utf-8').decode(buf)
  const p = Papa.parse<Row>(text, { header: true, skipEmptyLines: 'greedy', transformHeader: (h) => h.replace(/^\uFEFF/, '').trim() })
  return { rows: p.data, fields: p.meta.fields ?? [], errors: p.errors.map((e) => ({ row: e.row, message: e.message })), bytes: buf.byteLength, sha }
}

const toNum = (v?: string) => (v === undefined || v === '' ? undefined : Number.isFinite(Number(v)) ? Number(v) : undefined)
const NUMERIC_TYPE = /int|float|double|decimal|numeric|number|real/i

/** column_types_json → columnas numéricas. Admite objeto {col: tipo} o lista [{name|column, type}]. */
function numericColumnsOf(raw: string | undefined): { cols: string[]; error?: string } {
  if (!raw || !raw.trim()) return { cols: [] }
  const j = parseJson<unknown>(raw)
  if (j === undefined) return { cols: [], error: 'column_types_json no es JSON válido' }
  if (Array.isArray(j)) {
    return {
      cols: j
        .map((e) => (e && typeof e === 'object' ? (e as Record<string, unknown>) : undefined))
        .filter((e): e is Record<string, unknown> => !!e && NUMERIC_TYPE.test(String(e.type ?? e.dtype ?? '')))
        .map((e) => String(e.name ?? e.column ?? '')),
    }
  }
  if (j && typeof j === 'object') return { cols: Object.entries(j as Record<string, unknown>).filter(([, t]) => NUMERIC_TYPE.test(String(t))).map(([c]) => c) }
  return { cols: [], error: 'column_types_json con formato no reconocido' }
}

function columnsOf(raw: string | undefined): { cols: string[]; error?: string } {
  if (!raw || !raw.trim()) return { cols: [] }
  const j = parseJson<unknown>(raw)
  if (j === undefined) return { cols: [], error: 'columns_json no es JSON válido' }
  if (Array.isArray(j)) return { cols: j.map((c) => (typeof c === 'string' ? c : String((c as Record<string, unknown>)?.name ?? ''))).filter(Boolean) }
  if (j && typeof j === 'object') return { cols: Object.keys(j as Record<string, unknown>) }
  return { cols: [], error: 'columns_json con formato no reconocido' }
}

/** foreign_keys_json = { "columna": "tabla_destino" }. */
function fksOf(raw: string | undefined): { fks: { column: string; table: string }[]; error?: string } {
  if (!raw || !raw.trim()) return { fks: [] }
  const j = parseJson<unknown>(raw)
  if (j === undefined) return { fks: [], error: 'foreign_keys_json no es JSON válido' }
  if (!j || typeof j !== 'object' || Array.isArray(j)) return { fks: [], error: 'foreign_keys_json no es un objeto { columna: tabla }' }
  const fks: { column: string; table: string }[] = []
  let bad = 0
  Object.entries(j as Record<string, unknown>).forEach(([column, table]) => (typeof table === 'string' && table ? fks.push({ column, table }) : bad++))
  return { fks, error: bad ? `${bad} entradas de foreign_keys_json sin tabla de texto` : undefined }
}

/** Normaliza un valor numérico ("1.0" → "1"). Solo se usa en columnas declaradas numéricas. */
const numKey = (v: string): string | undefined => {
  const n = Number(v.trim())
  return v.trim() !== '' && Number.isFinite(n) ? String(n) : undefined
}

/** Valores de una celda; admite listas JSON (p. ej. possible_maps). Vacío = sin dato. */
function cellValues(v: string | undefined): string[] {
  if (v === undefined || v === '') return []
  if (v.trim().startsWith('[')) {
    const arr = parseJson<unknown>(v)
    if (Array.isArray(arr)) return arr.filter((x) => x !== null && x !== '').map(String)
  }
  return [v]
}

export async function runPackageCheck(): Promise<PackageReport> {
  const checkedAt = new Date().toLocaleString('es-ES')
  let control: Row[] = []
  let controlError: string | undefined
  const controlProblems: string[] = []
  try {
    const c = await fetchParsed(controlFile.url)
    control = c.rows
    if (c.errors.length) controlProblems.push(`00_control.csv: ${c.errors.length} errores de lectura (ej. fila ${c.errors[0].row}: ${c.errors[0].message})`)
  } catch (e) {
    controlError = e instanceof Error ? e.message : String(e)
  }

  const recordTypes: Record<string, number> = {}
  control.forEach((r) => (recordTypes[r.record_type || '(vacío)'] = (recordTypes[r.record_type || '(vacío)'] ?? 0) + 1))
  const datasetRows = control.filter((r) => r.record_type === 'dataset')
  const fileRows = control.filter((r) => r.record_type === 'file')
  if (!controlError) {
    if (datasetRows.length !== EXPECTED_DATASET_ROWS) controlProblems.push(`Filas dataset: ${datasetRows.length} (se esperaba ${EXPECTED_DATASET_ROWS})`)
    if (fileRows.length !== EXPECTED_FILE_ROWS) controlProblems.push(`Filas file: ${fileRows.length} (se esperaban ${EXPECTED_FILE_ROWS})`)
    const other = control.filter((r) => r.record_type !== 'dataset' && r.record_type !== 'file')
    if (other.length) controlProblems.push(`${other.length} filas con otro record_type: ${Array.from(new Set(other.map((r) => r.record_type || '(vacío)'))).join(', ')}`)
  }
  const dsRaw = datasetRows[0]?.details_json
  const dsJson = dsRaw ? parseJson<unknown>(dsRaw) : undefined
  const dataset = datasetRows.length ? { details: dsJson, raw: dsRaw, parseError: dsRaw && dsJson === undefined ? 'details_json de la fila dataset no es JSON válido' : undefined } : undefined

  const ctlByFile = new Map(fileRows.map((r) => [(r.filename ?? '').trim(), r]))
  const fileByTable = new Map<string, string>()
  const pkByTable = new Map<string, string>()
  const numByTable = new Map<string, Set<string>>()
  fileRows.forEach((r) => {
    const fn = (r.filename ?? '').trim()
    const table = (r.table ?? '').trim()
    const det = parseJson<Record<string, unknown>>(r.details_json ?? '')
    const pk = det && typeof det.primary_key === 'string' ? det.primary_key : 'id'
    if (table) {
      fileByTable.set(table, fn)
      pkByTable.set(table, pk)
      numByTable.set(table, new Set(numericColumnsOf(r.column_types_json).cols))
    }
  })

  const parsed = await Promise.all(
    packageFiles.map(async (f) => {
      try {
        return { f, p: await fetchParsed(f.url) as Parsed | undefined, err: undefined as string | undefined }
      } catch (e) {
        return { f, p: undefined, err: e instanceof Error ? e.message : String(e) }
      }
    }),
  )
  const parsedByFile = new Map(parsed.map((x) => [x.f.filename, x.p]))

  const valueSets = new Map<string, Set<string>>()
  const valuesOf = (table: string, col: string, numeric: boolean) => {
    const key = `${table}|${col}|${numeric}`
    if (!valueSets.has(key)) {
      const p = parsedByFile.get(fileByTable.get(table) ?? '')
      const set = new Set<string>()
      p?.rows.forEach((r) => {
        const v = r[col]
        if (v === undefined || v === '') return
        set.add(numeric ? numKey(v) ?? v : v)
      })
      valueSets.set(key, set)
    }
    return valueSets.get(key)!
  }

  const files: FileCheck[] = parsed.map(({ f, p, err }) => {
    const ctl = ctlByFile.get(f.filename)
    const problems: string[] = []
    const table = ctl?.table?.trim() || undefined
    const det = ctl ? parseJson<Record<string, unknown>>(ctl.details_json ?? '') : undefined
    if (ctl && ctl.details_json && !det) problems.push('details_json no es JSON válido')
    const primaryKey = det && typeof det.primary_key === 'string' ? det.primary_key : 'id'
    const cols = columnsOf(ctl?.columns_json)
    const nums = numericColumnsOf(ctl?.column_types_json)
    const fkDef = fksOf(ctl?.foreign_keys_json)
    ;[cols.error, nums.error, fkDef.error].forEach((e) => e && problems.push(e))
    if (!ctl && !controlError) problems.push('Sin fila record_type = file en 00_control.csv')
    const numSet = new Set(nums.cols)

    const base: FileCheck = {
      filename: f.filename,
      table,
      primaryKey,
      loaded: !!p,
      expectedRows: toNum(ctl?.expected_rows),
      expectedBytes: toNum(ctl?.bytes),
      expectedSha: ctl?.sha256?.trim() || undefined,
      expectedColumns: cols.cols,
      missingColumns: [],
      extraColumns: [],
      numericColumns: nums.cols,
      nonNumeric: [],
      duplicateIds: 0,
      duplicateSamples: [],
      emptyIds: 0,
      parseErrors: 0,
      parseErrorSamples: [],
      fks: [],
      controlProblems: problems,
    }
    if (!p) return { ...base, error: err ?? 'No se pudo descargar' }

    // Clave primaria: duplicados y vacíos (comparación exacta; numérica solo si la columna es numérica).
    let dup = 0
    let empty = 0
    const dupSamples: string[] = []
    if (p.fields.includes(primaryKey)) {
      const seen = new Set<string>()
      p.rows.forEach((r) => {
        const raw = r[primaryKey]
        if (raw === undefined || raw === '') return void empty++
        const v = numSet.has(primaryKey) ? numKey(raw) ?? raw : raw
        if (seen.has(v)) {
          dup++
          if (dupSamples.length < 6) dupSamples.push(raw)
        } else seen.add(v)
      })
    } else problems.push(`La clave primaria "${primaryKey}" no existe en el archivo`)

    // Columnas numéricas declaradas con valores no numéricos.
    const nonNumeric = nums.cols
      .filter((c) => p.fields.includes(c))
      .map((c) => {
        const bad = p.rows.map((r) => r[c]).filter((v): v is string => v !== undefined && v !== '' && !v.trim().startsWith('[') && numKey(v) === undefined)
        return { column: c, count: bad.length, samples: Array.from(new Set(bad)).slice(0, 4) }
      })
      .filter((x) => x.count > 0)

    const fks: FkResult[] = fkDef.fks.map(({ column, table: tt }) => {
      const targetColumn = pkByTable.get(tt) ?? 'id'
      const numeric = numSet.has(column) && (numByTable.get(tt)?.has(targetColumn) ?? false)
      const out: FkResult = { column, targetTable: tt, targetColumn, checked: 0, missing: 0, samples: [], numeric }
      const tf = fileByTable.get(tt)
      if (!tf) return { ...out, error: `La tabla "${tt}" no tiene fila file en el control` }
      const tp = parsedByFile.get(tf)
      if (!tp) return { ...out, error: `${tf} no se pudo cargar: relación no comprobada` }
      if (!p.fields.includes(column)) return { ...out, error: `La columna ${column} no existe en el archivo` }
      if (!tp.fields.includes(targetColumn)) return { ...out, error: `La clave ${targetColumn} no existe en ${tf}` }
      const target = valuesOf(tt, targetColumn, numeric)
      const bad: string[] = []
      p.rows.forEach((r) =>
        cellValues(r[column]).forEach((v) => {
          out.checked++
          if (!target.has(numeric ? numKey(v) ?? v : v)) bad.push(v)
        }),
      )
      return { ...out, missing: bad.length, samples: Array.from(new Set(bad)).slice(0, 6) }
    })

    return {
      ...base,
      rows: p.rows.length,
      bytes: p.bytes,
      sha256: p.sha,
      missingColumns: cols.cols.filter((c) => !p.fields.includes(c)),
      extraColumns: cols.cols.length ? p.fields.filter((c) => !cols.cols.includes(c)) : [],
      nonNumeric,
      duplicateIds: dup,
      duplicateSamples: dupSamples,
      emptyIds: empty,
      parseErrors: p.errors.length,
      parseErrorSamples: p.errors.slice(0, 4).map((e) => `fila ${e.row ?? '—'}: ${e.message}`),
      fks,
      controlProblems: problems,
    }
  })

  const pkgNames = new Set(packageFiles.map((f) => f.filename))
  return {
    controlLoaded: !controlError,
    controlError,
    controlRows: control.length,
    recordTypes,
    dataset,
    controlProblems,
    files,
    notInControl: controlError ? [] : packageFiles.map((f) => f.filename).filter((f) => !ctlByFile.has(f)),
    notInPackage: Array.from(ctlByFile.keys()).filter((f) => !pkgNames.has(f)),
    checkedAt,
  }
}

export type FileVerdict = 'ok' | 'diff' | 'error'

export function fileVerdict(f: FileCheck): FileVerdict {
  if (!f.loaded) return 'error'
  const rowsOk = f.expectedRows === undefined || f.rows === f.expectedRows
  const bytesOk = f.expectedBytes === undefined || f.bytes === f.expectedBytes
  const shaOk = !f.expectedSha || !f.sha256 || f.sha256.toLowerCase() === f.expectedSha.toLowerCase()
  const fkOk = f.fks.every((k) => k.missing === 0 && !k.error)
  return rowsOk && bytesOk && shaOk && !f.missingColumns.length && fkOk && !f.duplicateIds && !f.emptyIds && !f.parseErrors && !f.nonNumeric.length && !f.controlProblems.length ? 'ok' : 'diff'
}

const shaText = (f: FileCheck) => (!f.expectedSha ? 'sin referencia' : !f.sha256 ? 'no calculable en este navegador' : f.sha256.toLowerCase() === f.expectedSha.toLowerCase() ? 'coincide' : `NO coincide (real ${f.sha256.slice(0, 12)}…)`)

export function packageReportText(r: PackageReport): string {
  const L: string[] = [`# COMPROBACIÓN DEL PAQUETE MU VANGUARD (exportación 08/10/2026) · ${r.checkedAt}`]
  L.push(r.controlLoaded ? `00_control.csv: cargado · ${r.controlRows} filas · record_type: ${Object.entries(r.recordTypes).map(([k, n]) => `${k}=${n}`).join(', ')}` : `00_control.csv: ERROR DE DESCARGA → ${r.controlError}`)
  r.controlProblems.forEach((p) => L.push(`  PROBLEMA DEL CONTROL: ${p}`))
  if (r.dataset?.parseError) L.push(`  ${r.dataset.parseError}`)
  const loaded = r.files.filter((f) => f.loaded).length
  const ok = r.files.filter((f) => fileVerdict(f) === 'ok').length
  L.push(`Archivos de datos: ${loaded}/${r.files.length} descargados · ${ok} sin diferencias · total del paquete con el control: ${loaded + (r.controlLoaded ? 1 : 0)}/48`)
  if (r.notInControl.length) L.push(`En el paquete sin fila file en el control: ${r.notInControl.join(', ')}`)
  if (r.notInPackage.length) L.push(`En el control pero no en el paquete: ${r.notInPackage.join(', ')}`)
  const fkTotal = r.files.reduce((n, f) => n + f.fks.length, 0)
  const fkBad = r.files.reduce((n, f) => n + f.fks.filter((k) => k.missing || k.error).length, 0)
  L.push(`Relaciones declaradas: ${fkTotal} · con referencias sin resolver o no comprobables: ${fkBad}`)
  L.push('')
  r.files.forEach((f) => {
    const v = fileVerdict(f)
    L.push(`## ${f.filename}${f.table ? ` (${f.table})` : ''} · ${v === 'ok' ? 'OK' : v === 'error' ? 'ERROR' : 'DIFERENCIA'}`)
    f.controlProblems.forEach((p) => L.push(`- control: ${p}`))
    if (!f.loaded) return void L.push(`- error de descarga: ${f.error}`)
    L.push(`- filas ${f.rows} / esperado ${f.expectedRows ?? '—'} · bytes ${f.bytes} / esperado ${f.expectedBytes ?? '—'} · sha256 ${shaText(f)}`)
    L.push(`- clave primaria ${f.primaryKey}: repetidas ${f.duplicateIds}${f.duplicateSamples.length ? ` (ej. ${f.duplicateSamples.join(', ')})` : ''} · vacías ${f.emptyIds}`)
    if (f.missingColumns.length) L.push(`- columnas que faltan: ${f.missingColumns.join(', ')}`)
    if (f.extraColumns.length) L.push(`- columnas no declaradas: ${f.extraColumns.join(', ')}`)
    f.nonNumeric.forEach((n) => L.push(`- columna numérica ${n.column}: ${n.count} valores no numéricos (ej. ${n.samples.join(', ')})`))
    if (f.parseErrors) L.push(`- errores de lectura: ${f.parseErrors} (${f.parseErrorSamples.join(' · ')})`)
    f.fks.forEach((k) =>
      L.push(`- FK ${k.column} → ${k.targetTable}.${k.targetColumn}${k.numeric ? ' [num]' : ' [texto]'}: ${k.error ? `NO COMPROBADA: ${k.error}` : `${k.checked} valores, ${k.missing} sin resolver${k.samples.length ? ` (ej. ${k.samples.join(', ')})` : ''}`}`),
    )
  })
  if (r.dataset) {
    L.push('', '# FILA DATASET (metadatos, instrucciones y pendientes del paquete; no es un archivo)')
    L.push(r.dataset.details !== undefined ? JSON.stringify(r.dataset.details, null, 2) : r.dataset.raw ?? '—')
  }
  return L.join('\n')
}
